import styled, { keyframes } from 'styled-components';
import React, { useEffect, useState } from "react";
import ActionPlanWizard from "./ActionPlanWizard";
import ActionPlanDashboard from "./ActionPlanDashboard";
import GroupDetailView from "./GroupDetailView";
import PlanDetailView from "./PlanDetailView";
import { ReactComponent as InnerLogicLogo } from "../../assets/images/full-logo.svg";

// InnerLogic Brand Colors (from colorTheme.js)
const brandColors = {
  primary: {
    50: "#EBF1FD",
    100: "#D5E2F9", 
    500: "#2D70E2",
    600: "#2254AA",
  },
  secondary: {
    500: "#27CDA7",
    600: "#1D9A7D",
  },
  text: {
    dark: "#2A3039",
    gray: "#666D79",
  },
  background: {
    white: "#FFFFFF",
    lightBlue: "#F8FAFF",
  },
  neutral: {
    200: "#F2F2F2",
    500: "#DFDFDF",
  }
};

// Define gradients to avoid template literal nesting issues
const brandGradient = `linear-gradient(135deg, ${brandColors.primary["500"]} 0%, ${brandColors.secondary["500"]} 100%)`;

// Mock data for prototype
const mockGroups = [
  {
    id: 1,
    name: "Q1 Leadership Development",
    created_at: "2024-01-15",
    member_count: 25,
    plans_sent: 22,
    on_track: 18,
    at_risk: 3,
    off_track: 1
  },
  {
    id: 2,
    name: "Sales Team Performance",
    created_at: "2024-02-01",
    member_count: 45,
    plans_sent: 45,
    on_track: 32,
    at_risk: 8,
    off_track: 5
  }
];

const mockPlans = [
  {
    id: 1,
    group_id: 1,
    member_name: "Sarah Johnson",
    department: "Engineering",
    status: "on-track",
    progress: 78,
    last_update: "2024-01-20",
    tasks_completed: 7,
    tasks_total: 10
  },
  {
    id: 2,
    group_id: 1,
    member_name: "Mike Chen",
    department: "Product",
    status: "at-risk",
    progress: 45,
    last_update: "2024-01-18",
    tasks_completed: 4,
    tasks_total: 9
  }
];

// Styled Components for Empty State
const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: "Raleway", sans-serif;
  background: ${brandColors.background.white};
`;

const CenteredContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px 20px;
  background: ${brandColors.background.white};
`;

const LogoSection = styled.div`
  margin-bottom: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledLogo = styled(InnerLogicLogo)`
  width: auto;
  height: 50px;
  
  @media (max-width: 768px) {
    height: 40px;
  }
`;

const MainHeading = styled.h1`
  font-size: 64px;
  font-weight: 500;
  color: ${brandColors.text.dark};
  margin: 0 0 32px 0;
  text-align: center;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 48px;
  }
  
  @media (max-width: 480px) {
    font-size: 36px;
  }
`;

const GradientText = styled.span`
  background: ${brandGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
`;

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  max-width: 500px;
  margin-bottom: 40px;
`;

const DescriptionText = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: ${props => props.secondary ? brandColors.text.gray : brandColors.text.dark};
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const StartPlanningButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: ${brandGradient};
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Raleway", sans-serif;
  box-shadow: 0 2px 8px rgba(45, 112, 226, 0.15);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(45, 112, 226, 0.25);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(45, 112, 226, 0.15);
  }
`;

const ViewDashboardButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: transparent;
  border: 2px solid ${brandColors.primary["500"]};
  border-radius: 8px;
  color: ${brandColors.primary["500"]};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Raleway", sans-serif;

  &:hover {
    background: ${brandColors.primary["50"]};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PlusIcon = styled.span`
  font-size: 20px;
  font-weight: bold;
  line-height: 1;
`;

const ActionHub = () => {
    const [currentView, setCurrentView] = useState('empty-state'); // 'empty-state', 'wizard', 'dashboard', 'group-detail', 'plan-detail'
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [groups, setGroups] = useState(mockGroups);

    const handleStartPlanning = () => {
        setCurrentView('wizard');
    };

    const handleViewDashboard = () => {
        setCurrentView('dashboard');
    };

    const handleBackToHub = () => {
        setCurrentView('empty-state');
        setSelectedGroup(null);
        setSelectedPlan(null);
    };

    const handleWizardComplete = (newGroup) => {
        setGroups(prev => [...prev, newGroup]);
        setCurrentView('dashboard');
    };

    const handleGroupSelect = (group) => {
        setSelectedGroup(group);
        setCurrentView('group-detail');
    };

    const handlePlanSelect = (plan) => {
        setSelectedPlan(plan);
        setCurrentView('plan-detail');
    };

    const renderEmptyState = () => (
        <Container>
            <CenteredContentWrapper>
                <LogoSection>
                    <StyledLogo />
                </LogoSection>

                <MainHeading>
                    Welcome to your <GradientText>Action Hub</GradientText>
                </MainHeading>

                <DescriptionSection>
                    <DescriptionText>
                        Create strategic action plans that drive meaningful change in your organization.
                        Turn insights into actionable roadmaps.
                    </DescriptionText>
                    <DescriptionText secondary>
                        Get started by creating your first action plan below.
                    </DescriptionText>
                </DescriptionSection>

                <ButtonGroup>
                    <StartPlanningButton onClick={handleStartPlanning}>
                        <PlusIcon>+</PlusIcon>
                        Start Planning
                    </StartPlanningButton>
                    
                    {groups.length > 0 && (
                        <ViewDashboardButton onClick={handleViewDashboard}>
                            📊 View Dashboard
                        </ViewDashboardButton>
                    )}
                </ButtonGroup>
            </CenteredContentWrapper>
        </Container>
    );

    return (
        <>
            {currentView === 'empty-state' && renderEmptyState()}
            {currentView === 'wizard' && (
                <ActionPlanWizard 
                    onBack={handleBackToHub} 
                    onComplete={handleWizardComplete}
                />
            )}
            {currentView === 'dashboard' && (
                <ActionPlanDashboard 
                    groups={groups}
                    plans={mockPlans}
                    selectedGroup={selectedGroup}
                    onBack={handleBackToHub}
                    onGroupSelect={handleGroupSelect}
                    onPlanSelect={handlePlanSelect}
                    onCreateNew={handleStartPlanning}
                />
            )}
            {currentView === 'group-detail' && (
                <GroupDetailView 
                    group={selectedGroup}
                    plans={mockPlans.filter(p => p.group_id === selectedGroup?.id)}
                    onBack={() => setCurrentView('dashboard')}
                    onPlanSelect={handlePlanSelect}
                />
            )}
            {currentView === 'plan-detail' && (
                <PlanDetailView 
                    plan={selectedPlan}
                    onBack={() => setCurrentView('group-detail')}
                />
            )}
        </>
    );
};

export default ActionHub;